import type { ReactNode } from 'react'
import { defineMessages, useT } from '../../i18n/index.ts'
import {
	IconCards,
	IconLessonPlan,
	IconLevels,
	IconPhoto,
	IconQuestionSheet,
	IconText,
	IconVideo,
	IconWheel,
} from '../../ui/Icon.tsx'
import { levelsMessages } from '../levels/messages.ts'
import { plansMessages } from '../plans/messages.ts'
import { wheelMessages } from '../wheel/messages.ts'

/** The route segment (`/tools/<key>`): seen-state key, tint and preview are keyed by it. */
export type ToolKey = 'exercises' | 'wheel' | 'words' | 'levels' | 'plans'

export type Tool = {
	id: string
	key: ToolKey
	title: string
	/** What the home screen calls it. An icon label is a name, never a sentence. */
	short: string
	blurb: string
	/** One line saying what it makes, never a repeat of its name. */
	line: string
	icon: ReactNode
	/** What this tool accepts, shown on the featured tile so the first screen answers it. */
	accepts?: { icon: ReactNode; label: string }[]
	/** `undefined` while a tool is still being built; the tile renders as a placeholder. */
	to?: string
	/** ISO date the tool shipped; drives the hub badge (30 days, or until opened). */
	addedOn?: string
	/** `updated` for a tool that existed before and gained something. */
	badge?: 'new' | 'updated'
}

const messages = defineMessages({
	en: {
		lineExercises: 'Exercises your class answers from one link',
		lineWheel: 'Pick a student fairly or split the class into groups',
		lineWords: 'Flashcards, a crossword and a word search',
		lineLevels: 'One text rewritten for three reading levels',
		linePlans: 'A timed lesson built around your source',
		exercisesTitle: 'Make exercises from a source',
		exercisesShort: 'Exercises',
		exercisesBlurb:
			'Paste a text, photograph a page, or drop a video link. Get a set of exercises you can edit line by line, then share one link with your class.',
		pastedText: 'Pasted text',
		photo: 'Photo of a page',
		video: 'Video link',
		vocabTitle: 'Make a vocabulary list from a source',
		vocabShort: 'Vocabulary',
		vocabBlurb:
			'Key words with meanings and translations. Share flashcards, a crossword and a word search with your class.',
	},
	ro: {
		lineExercises: 'Exerciții la care clasa răspunde dintr-un singur link',
		lineWheel: 'Alege corect un elev sau împarte clasa în grupe',
		lineWords: 'Cartonașe, cuvinte încrucișate și căutare de cuvinte',
		lineLevels: 'Același text, rescris pe trei niveluri de citire',
		linePlans: 'O lecție cronometrată, construită pe sursa ta',
		exercisesTitle: 'Creează exerciții dintr-o sursă',
		exercisesShort: 'Exerciții',
		exercisesBlurb:
			'Lipește un text, fotografiază o pagină sau adaugă un link video. Primești un set de exerciții pe care le poți edita rând cu rând, apoi trimiți clasei un singur link.',
		pastedText: 'Text lipit',
		photo: 'Fotografia unei pagini',
		video: 'Link video',
		vocabTitle: 'Creează o listă de vocabular dintr-o sursă',
		vocabShort: 'Vocabular',
		vocabBlurb:
			'Cuvinte-cheie cu sensuri și traduceri. Trimite clasei cartonașe, cuvinte încrucișate și căutare de cuvinte.',
	},
	ru: {
		lineExercises: 'Задания, которые класс выполняет по одной ссылке',
		lineWheel: 'Честно выбрать ученика или разделить класс на группы',
		lineWords: 'Карточки, кроссворд и поиск слов',
		lineLevels: 'Один текст на трёх уровнях сложности',
		linePlans: 'Урок по минутам на основе вашего материала',
		exercisesTitle: 'Упражнения из материала',
		exercisesShort: 'Упражнения',
		exercisesBlurb:
			'Вставьте текст, сфотографируйте страницу или добавьте ссылку на видео. Получите набор упражнений, который можно править построчно, и поделитесь с классом одной ссылкой.',
		pastedText: 'Вставленный текст',
		photo: 'Фото страницы',
		video: 'Ссылка на видео',
		vocabTitle: 'Словарь из материала',
		vocabShort: 'Словарь',
		vocabBlurb:
			'Ключевые слова со значениями и переводом. Поделитесь с классом карточками, кроссвордом и поиском слов.',
	},
	fr: {
		lineExercises: 'Des exercices que la classe fait depuis un seul lien',
		lineWheel: 'Tirer un élève au sort ou former des groupes',
		lineWords: 'Cartes, mots croisés et mots mêlés',
		lineLevels: 'Un même texte réécrit à trois niveaux de lecture',
		linePlans: 'Une leçon minutée, construite sur votre source',
		exercisesTitle: 'Créer des exercices à partir d’une source',
		exercisesShort: 'Exercices',
		exercisesBlurb:
			'Collez un texte, photographiez une page ou ajoutez un lien vidéo. Obtenez une série d’exercices modifiable ligne par ligne, puis partagez un seul lien avec votre classe.',
		pastedText: 'Texte collé',
		photo: 'Photo d’une page',
		video: 'Lien vidéo',
		vocabTitle: 'Créer une liste de vocabulaire à partir d’une source',
		vocabShort: 'Vocabulaire',
		vocabBlurb:
			'Mots clés avec sens et traductions. Partagez cartes, mots croisés et mots mêlés avec votre classe.',
	},
	it: {
		lineExercises: 'Esercizi a cui la classe risponde da un solo link',
		lineWheel: 'Estrai uno studente o dividi la classe in gruppi',
		lineWords: 'Schede, cruciverba e cerca parole',
		lineLevels: 'Lo stesso testo riscritto su tre livelli di lettura',
		linePlans: 'Una lezione a tempi, costruita sulla tua fonte',
		exercisesTitle: 'Crea esercizi da una fonte',
		exercisesShort: 'Esercizi',
		exercisesBlurb:
			'Incolla un testo, fotografa una pagina o inserisci il link di un video. Ottieni una serie di esercizi che puoi modificare riga per riga, poi condividi un solo link con la tua classe.',
		pastedText: 'Testo incollato',
		photo: 'Foto di una pagina',
		video: 'Link di un video',
		vocabTitle: 'Crea una lista di vocaboli da una fonte',
		vocabShort: 'Vocabolario',
		vocabBlurb:
			'Parole chiave con significato e traduzione. Condividi con la classe schede, cruciverba e cerca parole.',
	},
	kk: {
		lineExercises: 'Сынып бір сілтеме арқылы орындайтын жаттығулар',
		lineWheel: 'Оқушыны әділ таңдау немесе сыныпты топтарға бөлу',
		lineWords: 'Карточкалар, кроссворд және сөз іздеу',
		lineLevels: 'Бір мәтін үш оқу деңгейінде',
		linePlans: 'Материалыңыз негізінде минутпен жоспарланған сабақ',
		exercisesTitle: 'Материалдан жаттығулар',
		exercisesShort: 'Жаттығулар',
		exercisesBlurb:
			'Мәтінді қойыңыз, бетті суретке түсіріңіз немесе бейне сілтемесін қосыңыз. Жолма-жол өңдеуге болатын жаттығулар жинағын алып, сыныппен бір сілтеме арқылы бөлісіңіз.',
		pastedText: 'Қойылған мәтін',
		photo: 'Беттің фотосы',
		video: 'Бейне сілтемесі',
		vocabTitle: 'Материалдан сөздік жасау',
		vocabShort: 'Сөздік',
		vocabBlurb:
			'Негізгі сөздер мағынасымен және аудармасымен. Сыныппен карточкалар, кроссворд және сөз іздеумен бөлісіңіз.',
	},
	es: {
		lineExercises: 'Ejercicios que la clase responde desde un solo enlace',
		lineWheel: 'Elige a un alumno al azar o divide la clase en grupos',
		lineWords: 'Tarjetas, crucigrama y sopa de letras',
		lineLevels: 'El mismo texto reescrito en tres niveles de lectura',
		linePlans: 'Una clase cronometrada a partir de tu material',
		exercisesTitle: 'Crear ejercicios a partir de un material',
		exercisesShort: 'Ejercicios',
		exercisesBlurb:
			'Pega un texto, fotografía una página o añade un enlace de video. Recibe una serie de ejercicios que puedes editar línea por línea y comparte un solo enlace con tu clase.',
		pastedText: 'Texto pegado',
		photo: 'Foto de una página',
		video: 'Enlace de video',
		vocabTitle: 'Crear una lista de vocabulario a partir de un material',
		vocabShort: 'Vocabulario',
		vocabBlurb:
			'Palabras clave con significado y traducción. Comparte con tu clase tarjetas, crucigrama y sopa de letras.',
	},
	pt: {
		lineExercises: 'Exercícios que a turma responde por um único link',
		lineWheel: 'Sorteie um aluno ou divida a turma em grupos',
		lineWords: 'Cartões, palavras cruzadas e caça-palavras',
		lineLevels: 'O mesmo texto reescrito em três níveis de leitura',
		linePlans: 'Uma aula cronometrada a partir do seu material',
		exercisesTitle: 'Criar exercícios a partir de um material',
		exercisesShort: 'Exercícios',
		exercisesBlurb:
			'Cole um texto, fotografe uma página ou adicione um link de vídeo. Receba uma série de exercícios que você edita linha por linha e compartilhe um único link com a turma.',
		pastedText: 'Texto colado',
		photo: 'Foto de uma página',
		video: 'Link de vídeo',
		vocabTitle: 'Criar uma lista de vocabulário a partir de um material',
		vocabShort: 'Vocabulário',
		vocabBlurb:
			'Palavras-chave com significado e tradução. Compartilhe com a turma cartões, palavras cruzadas e caça-palavras.',
	},
	de: {
		lineExercises: 'Übungen, die deine Klasse über einen Link löst',
		lineWheel: 'Ein Kind fair auslosen oder die Klasse in Gruppen teilen',
		lineWords: 'Karteikarten, Kreuzworträtsel und Wortsuche',
		lineLevels: 'Ein Text, umgeschrieben für drei Lesestufen',
		linePlans: 'Eine getaktete Stunde aus deinem Material',
		exercisesTitle: 'Übungen aus Material erstellen',
		exercisesShort: 'Übungen',
		exercisesBlurb:
			'Füge einen Text ein, fotografiere eine Seite oder gib einen Videolink an. Du bekommst ein Übungsset, das du Zeile für Zeile bearbeiten kannst, und teilst einen einzigen Link mit deiner Klasse.',
		pastedText: 'Eingefügter Text',
		photo: 'Foto einer Seite',
		video: 'Videolink',
		vocabTitle: 'Wortschatzliste aus Material erstellen',
		vocabShort: 'Wortschatz',
		vocabBlurb:
			'Schlüsselwörter mit Bedeutung und Übersetzung. Teile Karteikarten, Kreuzworträtsel und Wortsuche mit deiner Klasse.',
	},
})

/**
 * The whole hub. Shipping the second tool is one entry in this list plus its own route --
 * nothing here knows what any tool does, and removing one leaves no trace elsewhere.
 */
export function useTools(): Tool[] {
	const t = useT(messages)
	const w = useT(wheelMessages)
	const levels = useT(levelsMessages)
	const plans = useT(plansMessages)
	return [
		{
			id: 'exercises',
			key: 'exercises',
			title: t.exercisesTitle,
			short: t.exercisesShort,
			blurb: t.exercisesBlurb,
			line: t.lineExercises,
			icon: <IconQuestionSheet className="size-6" />,
			accepts: [
				{ icon: <IconText className="size-4" />, label: t.pastedText },
				{ icon: <IconPhoto className="size-4" />, label: t.photo },
				{ icon: <IconVideo className="size-4" />, label: t.video },
			],
			to: '/tools/exercises',
			addedOn: '2026-10-13',
			badge: 'updated',
		},
		{
			id: 'wheel',
			key: 'wheel',
			title: w.title,
			short: w.short,
			blurb: w.title,
			line: t.lineWheel,
			icon: <IconWheel className="size-6" />,
			to: '/tools/wheel',
			addedOn: '2026-10-13',
		},
		{
			id: 'vocabulary',
			key: 'words',
			title: t.vocabTitle,
			short: t.vocabShort,
			blurb: t.vocabBlurb,
			line: t.lineWords,
			icon: <IconCards className="size-6" />,
			to: '/tools/words',
			addedOn: '2026-10-13',
		},
		{
			id: 'levels',
			key: 'levels',
			title: levels.hubTitle,
			short: levels.hubShort,
			blurb: levels.hubBlurb,
			line: t.lineLevels,
			icon: <IconLevels className="size-6" />,
			to: '/tools/levels',
			addedOn: '2026-10-13',
		},
		{
			id: 'plans',
			key: 'plans',
			title: plans.hubTitle,
			short: plans.hubShort,
			blurb: plans.hubBlurb,
			line: t.linePlans,
			icon: <IconLessonPlan className="size-6" />,
			to: '/tools/plans',
			addedOn: '2026-10-13',
		},
	]
}
